<template>
	<AppBottomSheet :active="active" @toggling-popup="handleToggle" :force-width="bottomSheetWidth">
		<ModalHeader  v-if="!isEligibleForWelcomeBonus"
			:title="getString('recharge_balance')"
			:button-text="getString('confirm_recharge')"
			@click-button="handleConfirm"
			:button-disabled="!isValidAmount"
			:button-loading="loading"/>
		<div class="recharge-container">
			<div v-if="isEligibleForWelcomeBonus" class="welcome-bonus-container">
				<div class="bonus-content">
					<div class="bonus-icon">🎁</div>
					<div class="bonus-text">
						<h3>{{getString('marketing_welcome_bonus_title')}}</h3>
						<p>{{getString('marketing_welcome_bonus_message')}}</p>
					</div>
				</div>
				<AppButton
					:text="getString('claim_bonus')"
					:loading="claimingBonus"
					@click="handleClaimBonus"/>
			</div>

			<div class="recharge-content" v-else>

				<div class="balance-and-input-row">
					<div class="balance-display">
						<label class="label-style">{{getString('current_balance')}}</label>
						<div class="balance-value">{{ currentBalanceFormatted }}</div>
					</div>

					<div class="amount-input-container">
						<TouchNumberInput
							:label="getString('enter_amount')"
							:value="currencyStore.dbToActualValue(rechargeAmount)"
							:min="0.5"
							:max="10000"
							:increment="1"
							:double-decimal="true"
							:symbol="currencyStore.currCurrency.symbol"
							:right-side-symbol="false"
							@changed-value="updateRechargeAmount"/>
					</div>
				</div>

				<div class="quick-amounts-container">
					<div class="quick-amounts-buttons">
						<button
							v-for="amount in quickAmounts"
							:key="amount"
							class="quick-amount-btn"
							:class="{ 'active': rechargeAmount === currencyStore.actualToDbValue(amount) }"
							@click="setQuickAmount(amount)">
							{{currencyStore.currCurrency.symbol}}{{ amount }}
						</button>
					</div>
				</div>

				<div class="auto-recharge-container">
					<p>{{getString('auto_recharge_description', [rechargeAmountFormatted])}}</p>
					<AppSwitch
						:checked="autoRecharge"
						@switched="toggleAutoRecharge"/>
				</div>

			</div>
		</div>
		<CheckoutInPlace
			:client-secret="clientSecret"
			@checkout-complete="handleCheckoutComplete"
			@toggling-popup="handleCheckoutToggle"/>
	</AppBottomSheet>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppButton from "@/shared/components/AppButton.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";
import {useLanguageStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import API from "@/shared/helpers/API";

export default {
	setup() {
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		return {
			userStore,
			currencyStore,
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification
		};
	},
	name: "RechargeBottomSheet",
	components: {AppBottomSheet, ModalHeader, AppSwitch, AppButton, TouchNumberInput, CheckoutInPlace},
	mixins: [WindowWidth],
	props: {
		active: {
			type: Boolean,
			default: false
		}
	},
	emits: ['close'],
	data() {
		return {
			rechargeAmount: 5000,
			autoRecharge: true,
			quickAmounts: [20, 50, 200],
			clientSecret: null,
			loading: false,
			claimingBonus: false
		}
	},
	computed: {
		currentOpenBalance() {
			if (!this.userStore.currBusiness) {
				return 0;
			}
			let openBalance = this.userStore.currBusiness.open_balance;
			if (openBalance) {
				openBalance = this.currencyStore.dbToActualValue(openBalance);
			}
			return openBalance;
		},
		currentBalanceFormatted() {
			if (!this.userStore.currBusiness) {
				return this.currencyStore.readableCurrencyValue(0);
			}
			return this.currencyStore.readableCurrencyValue(this.userStore.currBusiness.open_balance || 0);
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		isValidAmount() {
			return this.rechargeAmount > 0;
		},
		isEligibleForWelcomeBonus() {
			if (!this.userStore.currBusiness) {
				return false;
			}
			const balance = this.userStore.currBusiness.open_balance || 0;
			const alreadyClaimed = this.userStore.currBusiness.marketing_welcome_bonus;
			return balance <= 0 && !alreadyClaimed;
		},
		rechargeAmountFormatted() {
			return this.currencyStore.readableCurrencyValue(this.rechargeAmount);
		}
	},
	methods: {
		handleToggle(active) {
			if (!active) {
				this.$emit('close', false);
			}
		},
		handleConfirm() {
			if (this.isValidAmount && !this.loading) {
				this.loading = true;
				API.init().stripeRechargeCredit(
					this.userStore.currBusinessId,
					this.rechargeAmount,
					this.autoRecharge,
					(response) => {
						this.loading = false;
						if (response.result === 'OK' && response.client_secret) {
							this.clientSecret = response.client_secret;
						} else {
							this.showNotification('generic-error');
						}
					}
				);
			}
		},
		handleCheckoutComplete() {
			this.clientSecret = null;
			setTimeout(() => {
				this.showNotification('generic-success');
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
				this.$emit('close', true);
			}, 2500);
		},
		handleCheckoutToggle(active) {
			if (!active) {
				this.clientSecret = null;
			}
		},
		updateRechargeAmount(value) {
			this.rechargeAmount = this.currencyStore.actualToDbValue(value);
		},
		setQuickAmount(amount) {
			this.rechargeAmount = this.currencyStore.actualToDbValue(amount);
		},
		toggleAutoRecharge(checked) {
			this.autoRecharge = checked;
		},
		handleClaimBonus() {
			if (this.claimingBonus) {
				return;
			}
			this.claimingBonus = true;
			API.init().claimMarketingWelcomeBonus(
				this.userStore.currBusinessId,
				(response) => {
					this.claimingBonus = false;
					if (response.result === 'OK') {
						this.showNotification('generic-success');
						this.userStore.refreshBusiness(this.userStore.currBusinessId);
						this.$emit('close', true);
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		},
		initData() {
			const savedAmount = this.userStore.currBusiness?.marketing_auto_recharge_amount;
			if (savedAmount && savedAmount > 0) {
				this.rechargeAmount = savedAmount;
				this.autoRecharge = true;
			} else {
				this.rechargeAmount = 5000;
				this.autoRecharge = false;
			}
			this.clientSecret = null;
			this.loading = false;
			this.claimingBonus = false;

			this.userStore.refreshBusiness(this.userStore.currBusinessId);
		}
	},
	watch: {
		active(newValue) {
			if (newValue) {
				this.initData();
			}
		}
	}
}
</script>

<style scoped lang="scss">
.recharge-container {
	display: flex;
	flex-direction: column;
	gap: 24px;
	width: 100%;
	padding: 0 4px;

	.welcome-bonus-container {
		display: flex;
		flex-direction: column;
		gap: 16px;
		padding: 20px;
		border-radius: 13px;

		.bonus-content {
			display: flex;
			gap: 16px;
			align-items: flex-start;

			.bonus-icon {
				font-size: 40px;
				flex-shrink: 0;

				@media (max-width: 450px) {
					font-size: 32px;
				}
			}

			.bonus-text {
				flex: 1;

				h3 {
					margin: 0 0 8px 0;
					font-size: 18px;
					font-weight: 600;
					color: var(--color-primary);

					@media (max-width: 450px) {
						font-size: 16px;
					}
				}

				p {
					margin: 0;
					font-size: 14px;
					color: var(--color-txt);
					line-height: 1.5;

					@media (max-width: 450px) {
						font-size: 13px;
					}
				}
			}
		}

		.base-button {
			width: 100%;
			max-width: unset;
			height: 42px;
		}
	}

	.recharge-content {
		display: flex;
		flex-direction: column;
		gap: 16px;
		width: 100%;
	}

	.balance-and-input-row {
		display: flex;
		gap: 16px;
		width: 100%;

		@media (max-width: 550px) {
			flex-direction: column;
		}

		.balance-display {
			flex: 1;
			display: flex;
			flex-direction: column;
			gap: 8px;
			padding: 12px;
			background-color: var(--color-alpha-1);
			border-radius: 13px;
			box-shadow: inset 1px 2px 4px var(--color-alpha-3);
			min-width: 0;

			.label-style {
				justify-content: center;
			}

			.balance-value {
				font-size: 28px;
				font-weight: 600;
				color: var(--color-primary);
				text-align: center;

				@media (max-width: 450px) {
					font-size: 24px;
				}
			}
		}

		.amount-input-container {
			flex: 1;
			min-width: 0;

			&:deep(.input-number-container) {
				flex-grow: 1;
			}
		}
	}

	.quick-amounts-container {
		display: flex;
		flex-direction: column;
		gap: 12px;

		.quick-amounts-buttons {
			display: flex;
			gap: 12px;
			justify-content: space-between;

			@media (max-width: 450px) {
				gap: 8px;
			}

			.quick-amount-btn {
				flex: 1;
				padding: 12px 16px;
				border-radius: 13px;
				border: 2px solid var(--color-alpha-3);
				background-color: var(--color-bg);
				color: var(--color-txt);
				font-size: 16px;
				font-weight: 500;
				cursor: pointer;
				transition: all 0.2s ease;

				@media (max-width: 450px) {
					padding: 10px 12px;
					font-size: 14px;
				}

				&:hover {
					border-color: var(--color-primary);
					background-color: var(--color-alpha-1);
				}

				&.active {
					border-color: var(--color-primary);
					background-color: var(--color-primary);
					color: white;
				}
			}
		}
	}

	.auto-recharge-container {
		padding-top: 8px;
		border-top: 1px solid var(--color-alpha-2);
		display: flex;
		justify-content: space-between;
		align-items: center;

		p {
			color: var(--color-txt);
			font-size: 14px;
			margin: 0;
		}
	}

	.label-style {
		display: flex;
		color: var(--txt-1);
		font-size: 10px;
		line-height: 15px;
		user-select: none;
		-webkit-user-select: none;
		-moz-user-select: none;
	}

	.mb-5 {
		margin-bottom: 5px;
	}

	.mb-10 {
		margin-bottom: 10px;
	}
}
</style>
